import { Language } from '../models/core.models';
import { Observable, firstValueFrom, from, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import {
  AuthApiService,
  AuthTokenResponse,
  LoginCredentials,
  LoginResponse,
  UserSession,
} from 'src/app/api/services/auth-api.service';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthReturnUrlService } from './auth-return-url.service';

export type {
  AuthTokenResponse,
  LoginCredentials,
  LoginResponse,
  UserSession,
} from 'src/app/api/services/auth-api.service';
export { LoginApiError } from 'src/app/api/services/auth-api.service';

export interface Credentials {
  token: string;
  /** Access token expiry as unix seconds — drives proactive renewal */
  expiresAt?: number;
}

const credentialsKey = 'sensika-auth';
const rememberKey = 'sensika-remember-me';
/** Web Locks name serializing refresh-token rotation across tabs */
const refreshLockKey = 'sensika-token-refresh';
/** Renew the access token this many seconds before it expires */
const proactiveRefreshMarginSeconds = 300;

/** Backend sends unix seconds; tolerate ms or numeric strings */
function normalizeEpochSeconds(
  value: string | number | undefined,
): number | undefined {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n > 1e12 ? Math.floor(n / 1000) : n;
}

/**
 * Auth client-side state: credential storage (session vs local by remember-me),
 * the cross-tab refresh lock, and the proactive-renewal timer. All backend
 * transport lives in `AuthApiService`.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private credentials: Credentials | null;

  private language: Language;

  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  private authApi = inject(AuthApiService);

  private authReturnUrl = inject(AuthReturnUrlService);

  constructor(private router: Router) {
    this.credentials = this.readStoredCredentials();
    this.scheduleProactiveRefresh();

    // Keep the in-memory copy in sync when another tab rotates the token
    // (fires only for localStorage changes, i.e. remember-me mode)
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === credentialsKey) {
        this.credentials = this.readStoredCredentials();
        this.scheduleProactiveRefresh();
      }
    });
  }

  private readStoredCredentials(): Credentials | null {
    try {
      return JSON.parse(
        sessionStorage.getItem(credentialsKey) ||
          localStorage.getItem(credentialsKey) ||
          'null',
      );
    } catch {
      return null;
    }
  }

  setLanguage(language: Language) {
    this.language = language;
  }

  getLanguage(): Language {
    return this.language;
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    return this.authApi.login(credentials);
  }

  clearCredentials() {
    this.cancelProactiveRefresh();
    this.credentials = null;
    sessionStorage.removeItem(credentialsKey);
    localStorage.removeItem(credentialsKey);
    // Clear per-tab session context
    sessionStorage.removeItem('sensika-tab-id');
    sessionStorage.removeItem('sensika-tab-company-id');
  }

  logout(): Observable<any> {
    // Revoke the session server-side first (needs the bearer token) and let the
    // backend expire the refresh cookie; local cleanup happens regardless.
    return this.authApi.logout().pipe(
      catchError(() => of(null)), // best-effort — never block logout on the API
      tap(() => {
        this.clearCredentials();
        this.authReturnUrl.rememberCurrent();
        this.router.navigate(['/auth/login']);
      }),
      map(() => ({ success: true, error: false })),
    );
  }

  verifyAuthToken(token: string): Observable<LoginResponse> {
    return this.authApi.verifyAuthToken(token);
  }

  hasCredentials(): boolean {
    return !!this.credentials && !!this.credentials.token;
  }

  getCredentials(): Credentials | null {
    return this.credentials;
  }

  getRememberMe(): boolean {
    const raw = localStorage.getItem(rememberKey);

    if (raw === null) {
      return false;
    }

    try {
      return JSON.parse(raw) === true;
    } catch {
      // cleanup invalid legacy values like "undefined"
      localStorage.removeItem(rememberKey);
      return false;
    }
  }

  setCredentials(credentials?: Credentials, remember?: boolean) {
    // When remember is not given (e.g. token refresh), keep the current mode —
    // previously a refresh silently moved the token to sessionStorage while the
    // stale one stayed in localStorage.
    const effectiveRemember = remember ?? this.getRememberMe();
    localStorage.setItem(rememberKey, JSON.stringify(effectiveRemember));
    this.credentials = credentials || { token: '' };
    if (credentials) {
      const storage = effectiveRemember ? localStorage : sessionStorage;
      const otherStorage = effectiveRemember ? sessionStorage : localStorage;
      storage.setItem(credentialsKey, JSON.stringify(credentials));
      otherStorage.removeItem(credentialsKey); // no stale copies
    } else {
      sessionStorage.removeItem(credentialsKey);
      localStorage.removeItem(credentialsKey);
    }
    this.scheduleProactiveRefresh();
  }

  loginToken(token: string): Observable<AuthTokenResponse> {
    return this.authApi.loginToken(token);
  }

  /**
   * Rotates the access token via the refresh cookie, serialized across tabs.
   *
   * The backend rotates the refresh token on every call, so two concurrent
   * refreshes (two tabs hitting 401 together) would invalidate each other. A
   * Web Lock serializes them; once inside the lock we re-read storage — if
   * another tab already renewed while we waited, reuse its token instead of
   * rotating again.
   *
   * @param staleToken the token that was rejected (or is about to expire)
   */
  refreshSession(staleToken?: string | null): Observable<string> {
    const doRefresh = async (): Promise<string> => {
      const stored = this.readStoredCredentials();
      if (stored?.token && staleToken && stored.token !== staleToken) {
        this.credentials = stored;
        this.scheduleProactiveRefresh();
        return stored.token;
      }

      const res = await firstValueFrom(this.authApi.refresh());
      this.setCredentials({
        token: res.access_token,
        expiresAt: normalizeEpochSeconds(res.access_token_expires_at),
      });
      return res.access_token;
    };

    const locks = (navigator as any)?.locks;
    return from(
      locks?.request
        ? (locks.request(refreshLockKey, doRefresh) as Promise<string>)
        : doRefresh(),
    );
  }

  private cancelProactiveRefresh(): void {
    if (this.proactiveRefreshTimer !== null) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
  }

  /**
   * Renews the access token shortly before it expires so users with an open
   * tab never hit a 401 round-trip. Reactive refresh (interceptor) remains the
   * fallback when this misses (laptop asleep, tab throttled, ...).
   */
  private scheduleProactiveRefresh(): void {
    this.cancelProactiveRefresh();

    const expiresAt = this.credentials?.expiresAt;
    if (!this.credentials?.token || !expiresAt) {
      return;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const delaySeconds = Math.max(
      expiresAt - proactiveRefreshMarginSeconds - nowSeconds,
      10,
    );

    this.proactiveRefreshTimer = setTimeout(() => {
      const currentToken = this.credentials?.token;
      this.refreshSession(currentToken).subscribe({
        // Errors are left to the reactive 401 flow (and logout on dead session)
        error: () => undefined,
      });
    }, delaySeconds * 1000);
  }

  getActiveSessions(): Observable<UserSession[]> {
    return this.authApi.getActiveSessions();
  }

  getGlobalActiveSessions(): Observable<UserSession[]> {
    return this.authApi.getGlobalActiveSessions();
  }

  revokeSession(sessionId: string | number): Observable<any> {
    return this.authApi.revokeSession(sessionId);
  }
}
